/**
 * Usage (board usage-r1): how much you built this week, whatever the agent, then each agent's limit and when it
 * resets where the agent tells us, your team, and what cpg.computer sees. Counts only, from your computer; the time
 * zone is this browser's. Built from the library only.
 */
import { AGENT_NAME, buildTime, dayBars, lastWeekWords, limitViews, noLimitWords, outOfLimit, seenWords, shortTime, tokenCount, weekHeadline } from '../../domain/usage'
import type { AgentWeek, FounderUsage } from '../../domain/schemas'
import type { Api } from '../../infra/api'
import { Card, EmptyState, ErrorNote, Grid, Inline, Meter, PageHeader, Skeleton, Stack, Text } from '../../ui'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'

/** Meter captions stay short; the words beside them say the rest. */
const CAPTION = { five_hour: '5 h', weekly: 'week', weekly_scoped: 'model' } as const

export function UsagePage({ api }: { api: Api }) {
  const u = useLoad(() => api.usage(), [api])
  const now = useNow(60_000)
  return (
    <Stack gap="lg">
      <PageHeader title="Usage" description="How much you build with your coding agents, and how close you are to their limits. Counted on your computer; only the numbers reach us." />
      {u.error && !u.data ? (
        <ErrorNote reason="Your usage didn't load just now." onRetry={u.reload} />
      ) : !u.data ? (
        <Stack gap="md" label="Loading">
          <Skeleton height="title" width="60%" />
          <Skeleton height="block" />
        </Stack>
      ) : (
        <UsageView usage={u.data} now={now} />
      )}
    </Stack>
  )
}

function UsageView({ usage, now }: { usage: FounderUsage; now: number }) {
  const agents = usage.me?.agents ?? []
  const out = outOfLimit(agents, now, usage.tz)
  const nothing = usage.week.activeMinutes === 0 && usage.week.sessions === 0 && agents.length === 0
  const team = usage.people.length > 1
  return (
    <Stack gap="lg">
      <Card title="This week">
        <Stack gap="md">
          <Text size="base" weight="head">{weekHeadline(usage.week)}</Text>
          <Inline gap="md">
            {dayBars(usage.week.days).map((d) => (
              <Stack key={d.day} gap="none" label={d.words}>
                <Text mono size="xs" tone="muted">{d.day}</Text>
                <Text mono size="base">{d.bar}</Text>
              </Stack>
            ))}
          </Inline>
          <Text tone="muted" size="sm">{[team ? 'Your whole team, hours per day in your time zone.' : 'Hours per day, in your time zone.', lastWeekWords(usage.previousWeek)].filter(Boolean).join(' ')}</Text>
        </Stack>
      </Card>

      {out && (
        <Card title="A limit ran out">
          <Text weight="head">{out}</Text>
        </Card>
      )}

      <Card title="Your agents" count={agents.length || undefined}>
        {nothing ? (
          <EmptyState title="Nothing counted yet.">
            Your computer counts as you build with Claude, Codex or Grok in Paseo. The first numbers show here within 15 minutes.
          </EmptyState>
        ) : agents.length === 0 ? (
          <Text tone="muted">Your own agents show here once you build with one on your computer.</Text>
        ) : (
          <Grid cols={agents.length >= 3 ? 3 : 2} gap="md">
            {agents.map((a) => (
              <AgentLine key={a.agent} agent={a} now={now} tz={usage.tz} />
            ))}
          </Grid>
        )}
      </Card>

      {team && (
        <Card title="Your team" count={usage.people.length}>
          <Stack gap="sm">
            {usage.people.map((p) => (
              <Inline key={p.personId} justify="between">
                <Text weight="head">{p.you ? `${p.name} (you)` : p.name}</Text>
                <Text mono size="sm" tone="muted">{`${shortTime(p.activeMinutes)} · ${p.commits} commit${p.commits === 1 ? '' : 's'}`}</Text>
              </Inline>
            ))}
            {usage.people.some((p) => p.sharesUser) && (
              <Text size="sm" tone="faint">People who share one login on your computer share one set of numbers.</Text>
            )}
          </Stack>
        </Card>
      )}

      <Card title="What cpg.computer sees">
        <Stack gap="sm">
          <Text>The same numbers you see here: time spent building, sessions, tokens, commits, deploys, and your agents' limits.</Text>
          <Text tone="muted">
            We never receive your prompts, your code, your files or their names, or what your agents say. Listen's investment team never sees usage.
          </Text>
          <Text size="sm">
            <a href="https://cpg.computer/privacy" target="_blank" rel="noreferrer">Read the privacy policy</a>
          </Text>
        </Stack>
      </Card>
    </Stack>
  )
}

function AgentLine({ agent: a, now, tz }: { agent: AgentWeek; now: number; tz: string }) {
  const limits = limitViews(a, now, tz)
  const seen = seenWords(a, now, tz)
  const fresh = a.tokens.input + a.tokens.output
  return (
    <Stack gap="sm">
      <Inline justify="between" align="baseline">
        <Text weight="head">{AGENT_NAME[a.agent]}</Text>
        <Text mono size="xs" tone="muted">{`${buildTime(a.activeMinutes)} · ${tokenCount(fresh)} tokens`}</Text>
      </Inline>
      {limits.length ? (
        limits.map((l) => (
          <Stack key={l.key} gap="xs">
            <Meter caption={CAPTION[l.key as keyof typeof CAPTION]} value={l.percent} full={90} label={`${AGENT_NAME[a.agent]}: ${l.name}`} />
            <Text size="sm" tone={l.full ? 'default' : 'muted'}>{`${l.name}: ${l.words}`}</Text>
          </Stack>
        ))
      ) : (
        <Text size="sm" tone="muted">{noLimitWords(a)}</Text>
      )}
      {seen && <Text size="xs" tone="faint">{seen}</Text>}
    </Stack>
  )
}
